/**
 * UserStreakPageV2 — redesigned User Streak lookup tool.
 *
 * Layout:
 *   • PageHeader with a single ghost "Clear" action (only visible
 *     when there's something to clear).
 *   • Two-column body on lg+: search FormSection on the left
 *     (email + days + Search/Restore), missing-dates info card on
 *     the right (only when there are gaps).
 *   • Below: streak table (per-day rows tinted teal for streak > 0
 *     and coral for streak == 0) wrapped in the v2 LibTable.
 *
 * Same Redux + action plumbing as v1 (fetchUserStreak /
 * restoreUserStreak / CLEAR_USER_STREAK).
 */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import {
  fetchUserStreak,
  restoreUserStreak,
} from '../../../actions/userStreakActions';
import {
  PageHeader,
  Button,
  LibTable,
  Modal,
  cn,
} from '../../redesign';
import {
  FormSection,
  FieldBlock,
  FieldRow,
  TextInput,
} from '../../redesign/edit';

const SearchIconSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const RestoreIconSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="1 4 1 10 7 10" />
    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
  </svg>
);

export default function UserStreakPageV2() {
  const dispatch = useDispatch();
  const user_streak = useSelector((s) => s.user_streak.user_streak);

  const [email, setEmail] = useState('');
  const [days, setDays] = useState(10);
  const [hasSearched, setHasSearched] = useState(false);
  const [missingDates, setMissingDates] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const streakData = Array.isArray(user_streak) ? user_streak : [];
  const hasAnySessions = streakData.some((it) => it.streak > 0);

  useEffect(() => {
    if (!streakData.length) {
      setMissingDates([]);
      return;
    }
    const next = Array.from(
      new Set(
        streakData.slice(0, days).filter((it) => it.streak === 0).map((it) => it.date),
      ),
    );
    setMissingDates(next);
  }, [user_streak, days, streakData]);

  const handleSearch = () => {
    setHasSearched(true);
    dispatch(fetchUserStreak({ user_email: email, days }));
  };

  const handleRestore = () => {
    if (!hasAnySessions) {
      setConfirmOpen(false);
      Swal.fire({
        icon: 'info',
        title: 'No sessions found',
        text: 'This user has no completed sessions to restore a streak from.',
      });
      return;
    }
    dispatch(restoreUserStreak({ user_email: email, days }));
    setConfirmOpen(false);
  };

  const handleClear = () => {
    setEmail('');
    setDays(10);
    setMissingDates([]);
    setHasSearched(false);
    dispatch({ type: 'CLEAR_USER_STREAK' });
  };

  let currentStreak = 0;
  let maxStreak = 0;
  streakData.forEach((it) => {
    if (it.streak > 0) {
      currentStreak += 1;
      if (currentStreak > maxStreak) maxStreak = currentStreak;
    } else {
      currentStreak = 0;
    }
  });

  const showClear = !!email || streakData.length > 0;

  return (
    <div className="bg-bg min-h-full">
      <PageHeader
        eyebrow="Users"
        title="User Streaks"
        actions={
          showClear ? (
            <Button variant="ghost" onClick={handleClear}>
              Clear
            </Button>
          ) : null
        }
      />

      <div
        className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px]"
        style={{ gap: 24, padding: '0 28px 32px', alignItems: 'start' }}
      >
        <section style={{ minWidth: 0 }}>
          <FormSection title="Lookup" num="01">
            <FieldRow>
              <FieldBlock label="User email" required htmlFor="f-streak-email" noMargin>
                <TextInput
                  id="f-streak-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@example.com"
                  autoFocus
                />
              </FieldBlock>
              <FieldBlock label="Number of days" htmlFor="f-streak-days" noMargin>
                <TextInput
                  id="f-streak-days"
                  type="number"
                  min={1}
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                />
              </FieldBlock>
            </FieldRow>
            <div className="flex items-center" style={{ gap: 8, marginTop: 14 }}>
              <Button
                variant="coral"
                leadingIcon={SearchIconSvg}
                onClick={handleSearch}
                disabled={!email}
              >
                Search streak
              </Button>
              {streakData.length > 0 && (
                <Button
                  variant="ghost"
                  leadingIcon={RestoreIconSvg}
                  onClick={() => setConfirmOpen(true)}
                >
                  Restore streak
                </Button>
              )}
            </div>
          </FormSection>

          {hasSearched && streakData.length === 0 && (
            <div
              className="bg-surface border border-sand text-text-muted italic text-center"
              style={{ borderRadius: 12, padding: '40px 16px', fontSize: 13, marginTop: 8 }}
            >
              No streak data found for this user.
            </div>
          )}

          {streakData.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div
                className="flex items-center"
                style={{ gap: 16, padding: '6px 4px 10px' }}
              >
                <Stat label="Current streak" value={maxStreak} suffix="days" />
                <Stat label="Days tracked" value={streakData.length} />
                <Stat label="Gaps" value={missingDates.length} tone={missingDates.length ? 'coral' : 'neutral'} />
              </div>
              <LibTable
                fixed
                className="border border-sand"
                style={{ borderRadius: 12, overflow: 'hidden' }}
              >
                <colgroup>
                  <col />
                  <col style={{ width: 140 }} />
                </colgroup>
                <LibTable.Head>
                  <tr>
                    <LibTable.Th>Date</LibTable.Th>
                    <LibTable.NumTh>Streak</LibTable.NumTh>
                  </tr>
                </LibTable.Head>
                <tbody>
                  {streakData.map((it) => {
                    const ok = it.streak > 0;
                    return (
                      <LibTable.Row key={it.date}>
                        <LibTable.Td>
                          <div className="flex items-center" style={{ gap: 8 }}>
                            <span
                              aria-hidden
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                background: ok ? '#75A293' : '#E58A6A',
                                display: 'inline-block',
                                flexShrink: 0,
                              }}
                            />
                            <span
                              className={cn('font-mono', ok ? 'text-navy' : 'text-text-muted')}
                              style={{ fontSize: 12.5 }}
                            >
                              {it.date}
                            </span>
                          </div>
                        </LibTable.Td>
                        <LibTable.NumTd muted={!ok}>{it.streak}</LibTable.NumTd>
                      </LibTable.Row>
                    );
                  })}
                </tbody>
              </LibTable>
            </div>
          )}
        </section>

        {/* Right rail — only when there are gaps */}
        {missingDates.length > 0 && (
          <aside
            className="bg-surface border border-sand"
            style={{
              alignSelf: 'start',
              borderRadius: 12,
              padding: 16,
              minWidth: 0,
            }}
          >
            <div
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 10.5, letterSpacing: '0.08em', marginBottom: 10 }}
            >
              Missing dates · {missingDates.length}
            </div>
            <div className="flex flex-wrap" style={{ gap: 4 }}>
              {missingDates.map((d) => (
                <span
                  key={d}
                  className="font-mono"
                  style={{
                    fontSize: 11,
                    padding: '2px 7px',
                    borderRadius: 999,
                    background: '#E58A6A18',
                    color: '#A5524B',
                    border: '1px solid #E58A6A33',
                  }}
                >
                  {d}
                </span>
              ))}
            </div>
          </aside>
        )}
      </div>

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        eyebrow="User Streaks"
        title="Confirm restore"
        maxWidth={460}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="coral" onClick={handleRestore}>
              Confirm
            </Button>
          </>
        }
      >
        <p className="text-text-muted" style={{ fontSize: 13, margin: 0 }}>
          Restore <code className="font-mono text-navy">{email}</code>'s streak for the past <strong>{days}</strong> days? Missing dates will be marked as completed.
        </p>
      </Modal>
    </div>
  );
}

function Stat({ label, value, suffix, tone = 'neutral' }) {
  const color = tone === 'coral' ? '#A5524B' : '#2C3E50';
  return (
    <div>
      <div
        className="font-condensed font-bold uppercase text-text-muted"
        style={{ fontSize: 10, letterSpacing: '0.08em', marginBottom: 2 }}
      >
        {label}
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color }}>
        {value}
        {suffix && (
          <span className="text-text-muted" style={{ fontSize: 11, marginLeft: 4, fontWeight: 400 }}>
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}
